import { useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { BlockContainer, FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { useOnceExecute } from '@r1-frontend/shared/hooks/useOnceExecute';

import { ConfirmNumberPopup } from '~/src/components/ConfirmNumberPopup';
import { PersonForm } from '~/src/components/PersonForm';
import { setClientName, setClientPhone } from '~/src/features/tariffBuilder/actions';
import setStep from '~/src/features/tariffBuilder/actions/setStep';
import { changePlanWebcabThunk } from '~/src/features/tariffBuilder/actions/thunk';
import { useConstructorAnalytics } from '~/src/features/tariffBuilder/analytics/hooks/useConstructorAnalytics';
import { GA_CATEGORY } from '~/src/features/tariffBuilder/constants/analytics';
import { useCtxDispatch, useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import { hasSelectedEquipment } from '~/src/features/tariffBuilder/selectors/hasSelectedEquipment';
import isContactDataValid from '~/src/features/tariffBuilder/selectors/isContactDataValid';
import selectCurrentTariff from '~/src/features/tariffBuilder/selectors/selectCurrentTariff';
import { ESteps } from '~/src/features/tariffBuilder/types/ESteps';
import { TState } from '~/src/store';
import { selectTerminalResource } from '~/src/store/selectors/service';

import CreatablePhoneSelectWrapper from './CreatablePhoneSelectWrapper';
import { TextBlock } from './TextBlock';
import { TextBlockAuth } from './TextBlockAuth';

import { PersonFormWrapper } from './styled';


const getActionBtnText = (isContactId: boolean, hasEquipment: boolean) => {
    if (!isContactId) {
        return 'Подтвердить';
    }
    if (hasEquipment) {
        return 'Продолжить';
    }
    return 'Сменить тариф';
};

const ContactsContainer = (): JSX.Element => {
    const dispatch = useCtxDispatch();

    const { name, phone, isDataValid, currentTariff, contactPhone, hasEquipment, loading } = useCtxSelector(state => ({
        name: state.client.name,
        phone: state.client.phone,
        contactPhone: state.client.contactPhone,
        currentTariff: selectCurrentTariff(state),
        isDataValid: isContactDataValid(state),
        hasEquipment: hasSelectedEquipment(state),
        loading: state.loading,
    }));

    const analytics = useConstructorAnalytics();

    const trId = useSelector((state: TState) => selectTerminalResource(state));

    const [isShowPopup, setIsShowPopup] = useState(false);

    const firstInputPhone = useOnceExecute(() => {
        analytics.personalVerification.enterPhoneNumber();
    });

    const firstInputFio = useOnceExecute(() => {
        analytics.personalVerification.enterFio();
    });

    const onChangeFio = (value: string) => {
        dispatch(setClientName(value));
        firstInputFio();
    };

    const onChangePhone = (value: string) => {
        dispatch(setClientPhone(value));
        firstInputPhone();
    };

    const onConfirmPhone = () => {
        if (currentTariff && !hasEquipment) {
            dispatch(changePlanWebcabThunk(trId));
        } else {
            dispatch(setStep(ESteps.CreateAgreement));
        }

        analytics.personalVerification.stepPersonalData('confirm');
    };

    const onCloseConfirmPopup = () => {
        setIsShowPopup(false);
    };

    const handleButtonClick = () => {
        if (currentTariff && contactPhone?.isContactId) {
            if (hasEquipment) {
                dispatch(setStep(ESteps.CreateAgreement));
            } else {
                dispatch(changePlanWebcabThunk(trId));
            }
            return;
        }
        setIsShowPopup(true);
        analytics.personalVerification.openedPersonalDataVerification();
    };

    return (
        <>
            {currentTariff ? <TextBlockAuth hasEquipment={hasEquipment}/> : <TextBlock/>}
            <FullWidthListContainer $size={16}>
                <BlockContainer
                    rounder={{
                        $desktop: 24,
                        $mobile: 16,
                    }}
                    indent={{
                        $united: [24],
                    }}
                >
                {currentTariff
                    ? <CreatablePhoneSelectWrapper fillPhone={phone} contactPhone={contactPhone} />
                    : <PersonFormWrapper>
                        <PersonForm
                            fio={name}
                            phone={phone}
                            onChangeFio={onChangeFio}
                            onChangePhone={onChangePhone}
                            isNeedToBlockInvalidFio={true}
                        />
                    </PersonFormWrapper>}
                </BlockContainer>
                <ButtonGroup>
                    <Button bType={'action'} onClick={handleButtonClick} disabled={!isDataValid || loading}>
                        {getActionBtnText(contactPhone?.value?.length, hasEquipment)}
                    </Button>
                </ButtonGroup>
            </FullWidthListContainer>

            <ConfirmNumberPopup
                isOpen={isShowPopup}
                onClosePopup={onCloseConfirmPopup}
                onConfirmed={onConfirmPhone}
                phone={phone}
                dataLayerPushCategory={GA_CATEGORY}
            />
        </>
    );
};

export default ContactsContainer;
